import React, { useState, type AriaAttributes, type Ref } from "react";
import { CalendarIcon } from "@icons";
import type { DatePickerFieldControlProps } from "./DatePicker.types";
import { toInputFormats } from "./DatePicker.lib";
import { commitDateInputValue } from "./commitDateInputValue";
import styles from "./DatePicker.module.scss";

interface DatePickerTriggerInputProps<TValue> {
    inputId: string;
    label?: string;
    formattedValue: string;
    editableValue: string;
    placeholder: string;
    disabled: boolean;
    showTriggerIcon: boolean;
    inputFormat: string | readonly string[];
    parseValue: (value: string) => TValue | undefined;
    emptyValue: TValue;
    onValueChange: (value: TValue) => void;
    fieldControlProps?: DatePickerFieldControlProps;
    ariaInvalid: AriaAttributes["aria-invalid"];
    ariaRequired: AriaAttributes["aria-required"];
    ref?: Ref<HTMLInputElement>;
}

/**
 * Contents of the `input` variant trigger: a typeable date field that shows the
 * formatted value at rest and the raw input format while editing, committing
 * on blur or Enter.
 */
export const DatePickerTriggerInput = <TValue,>({
    inputId,
    label,
    formattedValue,
    editableValue,
    placeholder,
    disabled,
    showTriggerIcon,
    inputFormat,
    parseValue,
    emptyValue,
    onValueChange,
    fieldControlProps,
    ariaInvalid,
    ariaRequired,
    ref,
}: DatePickerTriggerInputProps<TValue>) => {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const formatHintId = `${inputId}-trigger-format`;
    const describedBy = [fieldControlProps?.["aria-describedby"], formatHintId]
        .filter(
            (describedById) =>
                describedById !== undefined && describedById !== "",
        )
        .join(" ");

    return (
        <>
            {showTriggerIcon && <CalendarIcon />}
            <span className={styles.triggerContent}>
                {label && (
                    <label htmlFor={inputId} className={styles.inputLabel}>
                        {label}
                    </label>
                )}
                <input
                    ref={ref}
                    id={inputId}
                    className={styles.control}
                    value={isEditing ? inputValue : formattedValue}
                    placeholder={placeholder}
                    disabled={disabled}
                    onFocus={() => {
                        setInputValue(editableValue);
                        setIsEditing(true);
                    }}
                    onChange={(event) => setInputValue(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key !== "Enter") {
                            return;
                        }
                        event.currentTarget.blur();
                    }}
                    onBlur={() => {
                        commitDateInputValue({
                            inputValue,
                            parseValue,
                            emptyValue,
                            onValueChange,
                        });
                        setIsEditing(false);
                    }}
                    aria-labelledby={fieldControlProps?.["aria-labelledby"]}
                    aria-describedby={describedBy}
                    aria-invalid={ariaInvalid}
                    aria-required={ariaRequired}
                />
            </span>
            <span id={formatHintId} className={styles.srOnly}>
                Expected format: {toInputFormats(inputFormat).join(" or ")}
            </span>
        </>
    );
};
